import { useEffect, useRef, useState, useImperativeHandle } from "react";
import ShipApiRepository from "repositories/api/ShipApiRepository";
import uuid from "react-uuid";
import { CardList } from "components/layout/card/CardList";
import Spacer from "components/layout/Spacer";
import { CardImage } from "@progress/kendo-react-layout";
import { formatDateTimeForDisplay } from "components/helpers/DateTimeHelpers";
import Overlay from "components/layout/overlay/Overlay";
import { isNullOrEmpty } from "components/helpers/ObjectHelpers";
import { PilotageDetailsShipDialog } from "./PilotageDetailsShipDialog";
import { usePilotageStoreContext } from "../../../components/hooks/usePilotageStoreContext";
import { useStoreSelector } from "../../../components/hooks/useStoreSelector";
import TitleContainer from "components/layout/TitleContainer";
import { getLoggedInPersonId } from "components/helpers/AuthHelpers";
import { useSuccessNotification, useErrorNotification } from 'components/hooks/useAppStore';

interface PilotageDetailsShipObservationsProps {
    ref: any;
    isAllowedEdit: boolean;
    shipId: number;
    pilotageId: number;
}

export const PilotageDetailsShipObservations = ({
    ref,
    isAllowedEdit,
    shipId,
    pilotageId,
}: PilotageDetailsShipObservationsProps) => {
    useImperativeHandle(ref, () => ({
        onAddObservation() {
            dialogRef.current.onAddObservation();
        },
    }));

    const [cards, setCards] = useState<any[]>([]);
    const listRef = useRef<any>(null);
    const dialogRef = useRef<any>(null);

    const [isBusy, setIsBusy] = useState(false);
    const [shipObservations, setShipObservations] = useState<any[]>([]);
    const [shipObservationTypes, setShipObservationTypes] = useState<any[]>([]);

    const pilotageStore = usePilotageStoreContext();
    const setHasObservation = useStoreSelector(pilotageStore, (state) => state.setHasObservation);

    const addSuccessNotification = useSuccessNotification();
    const addErrorNotification = useErrorNotification();

    const fetchShipObservations = async () => {
        const imageWidth = Math.floor(window.innerWidth * 1);
        const imageHeight = Math.floor(window.innerHeight * 1);

        const response: any = await ShipApiRepository.getShipObservationsAsync(shipId, imageWidth, imageHeight);
        if (response.ok === true) {
            const data = await response.json();
            setShipObservations(data);
            setHasObservation(data.length > 0);
        }
    };

    const fetchShipObservationTypes = async () => {
        const response: any = await ShipApiRepository.getShipObservationTypesAsync();
        if (response.ok === true) {
            const data = await response.json();

            setShipObservationTypes(data);
        }
    };

    const fetchData = async () => {
        await fetchShipObservations();
        await fetchShipObservationTypes();
    };

    const fetchObservation: any = async (shipObservation: any) => {
        const desiredImageWidth = Math.floor(window.innerWidth) * 2;
        const desiredImageHeight = Math.floor(window.innerHeight) * 2;

        try {
            const response: any = await ShipApiRepository.getShipObservationAsync(
                shipObservation.shipId,
                shipObservation.shipObservationId,
                desiredImageWidth,
                desiredImageHeight,
            );
            const data = await response.json();
            return data;
        } catch (error) {
            addErrorNotification("En feil oppstod ved henting av observasjoner.");
            console.error("An error occurred when fetching observation", error);
        }
    };

    const deleteObservation = async (observationToBeDeleted: any) => {
        setIsBusy(true);
        try {
            const observation = await fetchObservation(observationToBeDeleted);

            if (!isNullOrEmpty(observation)) {
                await ShipApiRepository.deleteShipObservationByIdAsync(
                    observation.shipObservationId,
                    observation.etagValue,
                );
            }
            listRef.current!.onDeleted([observation.shipObservationId]);
            addSuccessNotification("Observasjonen ble slettet");
            fetchData();
        } catch (error) {
            addErrorNotification("Kunne ikke slette observasjon");
            console.error("An error occurred when deleting observation", error);
        }
        setIsBusy(false);
    };

    function isLoggedInPersonCreaterOfObservation(observationCreatorPerson: any) {
        if(isNullOrEmpty(observationCreatorPerson)) return true;
        return observationCreatorPerson.modifiedByPersonId === getLoggedInPersonId();
    }

    useEffect(() => {
        if (shipId === 0) return;
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shipId]);

    useEffect(() => {
        function generateCards() {
            const result = [];

            for (const shipObservation of shipObservations) {
                const guid = uuid();

                result.push({
                    key: guid,
                    id: shipObservation.shipObservationId,
                    title: formatDateTimeForDisplay(shipObservation.modifiedByPerson.modifiedDate),
                    subTitle: shipObservation.modifiedByPerson.name,
                    borderColor: shipObservation.shipObservationType.color,
                    backgroundColor: "var(--tertiary-bg)",
                    showIndicator: true,
                    indicatorPrimaryColor: shipObservation.shipObservationType.color,
                    onClick: () => dialogRef.current.onEditObservation(shipObservation),
                    canDelete: isAllowedEdit && shipObservation.shipObservationType.isVisible !== false && isLoggedInPersonCreaterOfObservation(shipObservation.modifiedByPerson),
                    onDelete: () => deleteObservation(shipObservation),
                    confirmDelete: true,
                    confirmDeleteTitle: "Slett observasjon",
                    confirmDeleteText: "Er du sikker p&aring; at du vil slette observasjon?",
                    content: <ShipObservationBody shipObservation={shipObservation} />,
                });
            }
            setCards(result);
        }

        generateCards();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shipObservations]);

    return (
        <>
            {!isNullOrEmpty(cards) && (
                <>
                    <TitleContainer title="Observasjoner" />
                    <div>
                        <Spacer height={10} />
                        <CardList ref={listRef} source={cards} noContentMessage="" />
                    </div>
                </>
            )}

            <Overlay isBusy={isBusy} />

            <PilotageDetailsShipDialog
                ref={dialogRef}
                fetchObservation={fetchObservation}
                reloadObservations={fetchShipObservations}
                shipObservationTypes={shipObservationTypes}
                isAllowedEdit={isAllowedEdit}
                shipId={shipId}
                pilotageId={pilotageId}
            />
        </>
    );
}

interface ShipObservationBodyProps {
    shipObservation: any;
}
const ShipObservationBody = ({ shipObservation }: ShipObservationBodyProps) => {

    const [width, setWidth] = useState(window.innerWidth * 0.9);
    const [height, setHeight] = useState(window.innerHeight * 0.25);

    useEffect(() => {
        const handleOrientationChange = () => {
            setWidth(window.innerWidth * 0.9);
            setHeight(window.innerHeight * 0.25);
        }

        window.screen.orientation.addEventListener('change', handleOrientationChange)
        return () => {
            window.screen.orientation.removeEventListener('change', handleOrientationChange);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="ob-card">
            {shipObservation.imageBase64Url && (
                <div className="ob-image-container">
                    <CardImage className="ob-image"
                        style={{
                            objectFit: "contain",
                            paddingTop: "5px",
                            maxWidth: width,
                            maxHeight: height
                        }}
                        src={shipObservation.imageBase64Url}
                    />
                </div>
            )}
            <div className="ob-card-body font-size-lg">
                {shipObservation.observation}
            </div>
        </div>
    );
}
